import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useActivity } from "@/context/ActivityContext";
import BackButton from "@/components/BackButton";
import PilotOfMonth from "@/components/PilotOfMonth";
import PilotMonthAlbo from "@/components/PilotMonthAlbo";
import PilotMonthBadge from "@/components/PilotMonthBadge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, ChevronRight, User, Search, Trash2 } from "lucide-react";

export default function PilotsList() {
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState("");
  const { isAdmin } = useAuth();
  const { tick } = useActivity();

  const removePilot = async (p) => {
    if (!window.confirm(`Eliminare il pilota "${p.name}" dall'app?`)) return;
    try {
      await api.delete(`/users/${p.user_id}`);
      toast.success("Pilota eliminato");
      setPilots((prev) => prev.filter((x) => x.user_id !== p.user_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  useEffect(() => {
    setErr(false);
    api.get("/pilots").then((r) => setPilots(r.data)).catch(() => setErr(true)).finally(() => setLoading(false));
  }, [tick]);

  const filtered = pilots.filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="pilots-list-page">
      <BackButton className="mb-6" />
      <PilotOfMonth />
      <PilotMonthAlbo />
      <div className="flex items-center gap-3 mb-8">
        <Users className="h-8 w-8 text-yellow-400 text-glow-yellow" />
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tighter text-zinc-50">Piloti</h1>
          <p className="text-sm text-zinc-500">Piloti iscritti e approvati. Tocca un pilota per la sua scheda completa.</p>
        </div>
      </div>

      {!loading && !err && pilots.length > 0 && (
        <div className="mb-6 flex items-center gap-2 border border-zinc-700 bg-zinc-950 px-3 max-w-md focus-within:border-yellow-400 transition-colors duration-200">
          <Search className="h-4 w-4 text-zinc-500" />
          <input data-testid="pilots-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca un pilota…" className="w-full bg-transparent py-3 text-zinc-100 outline-none placeholder:text-zinc-600" />
        </div>
      )}

      {loading ? (
        <p className="text-zinc-500 py-10 text-center">Caricamento…</p>
      ) : err ? (
        <p className="text-red-400 py-10 text-center" data-testid="pilots-error">Impossibile caricare i piloti. Riprova più tardi.</p>
      ) : pilots.length === 0 ? (
        <p className="text-zinc-500 py-10 text-center" data-testid="pilots-empty">Nessun pilota approvato ancora.</p>
      ) : filtered.length === 0 ? (
        <p className="text-zinc-500 py-10 text-center" data-testid="pilots-no-match">Nessun pilota trovato per "{q}".</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pilots-grid">
          {filtered.map((p, i) => (
            <motion.div key={p.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="relative">
              <Link to={`/piloti/${encodeURIComponent(p.name)}`} data-testid={`pilot-card-${p.name}`}
                className="group flex items-center gap-4 border border-zinc-800 bg-zinc-900 p-5 hover:border-yellow-400/50 transition-colors duration-200">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-zinc-800 border border-zinc-700 group-hover:border-yellow-400/50 transition-colors duration-200">
                  <User className="h-6 w-6 text-zinc-400 group-hover:text-yellow-400 transition-colors duration-200" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-lg font-bold text-zinc-50 truncate">{p.name}</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                  {p.race_type ? (
                    <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-cyan-400">{p.race_type === "entrambe" ? "RC + SIM" : p.race_type.toUpperCase()}</span>
                  ) : (
                    <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-500">Pilota</span>
                  )}
                  <PilotMonthBadge name={p.name} />
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-zinc-600 group-hover:text-yellow-400 group-hover:translate-x-0.5 transition-all duration-200" />
              </Link>
              {isAdmin && p.user_id && p.role !== "admin" && (
                <button onClick={() => removePilot(p)} aria-label={`Elimina pilota ${p.name}`}
                  data-testid={`del-pilot-${p.name}`}
                  className="absolute top-2 right-2 p-2 rounded-md bg-zinc-950/70 text-zinc-400 hover:text-red-400 transition-colors duration-200 z-10">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
